import { useState, useEffect, useRef } from "react";
import "@/App.css";
import { Play, Pause, Mail, ExternalLink, Volume2, VolumeX, Send, Check } from "lucide-react";

const socialLinks = [
  { name: "Facebook", url: "https://www.facebook.com/fungistmusic", icon: "facebook" },
  { name: "Instagram", url: "https://www.instagram.com/fungistvgm/", icon: "instagram" },
  { name: "SoundCloud", url: "https://soundcloud.com/b-laze", icon: "soundcloud" },
  { name: "YouTube", url: "https://www.youtube.com/@B-laze_Fungist", icon: "youtube" },
  { name: "TikTok", url: "https://www.tiktok.com/@fungist_", icon: "tiktok" },
  { name: "X/Twitter", url: "https://twitter.com/Fungist_", icon: "twitter" },
  { name: "Spotify (Fungist)", url: "https://open.spotify.com/artist/2gP1lLdF0pETqbrilpdFZw", icon: "spotify" },
  { name: "Spotify (B-laze)", url: "https://open.spotify.com/artist/2boGGnhZJWVJFiMa2ZgCCZ", icon: "spotify" },
  { name: "LinkedIn", url: "https://www.linkedin.com/in/fungist-aka-b-laze-7b7b76302/", icon: "linkedin" },
  { name: "Newgrounds", url: "https://b-laze.newgrounds.com/", icon: "newgrounds" },
];

const youtubeVideos = [
  { id: "t0bOi2b7VSc", title: "FungistVGM Demo Promo" },
  { id: "VPxwttT_6w0", title: "Great News" },
  { id: "vdyFQIX_p2A", title: "Epic Melody" },
  { id: "6ApuJ8vZ4Qg", title: "Well Met" },
];

const demoReels = [
  { src: "https://customer-assets.emergentagent.com/job_boring-kilby-5/artifacts/0hutwrtl_Trunko.mov", title: "Trunko OST", type: "video" },
  { src: "https://customer-assets.emergentagent.com/job_boring-kilby-5/artifacts/gjqlfzxt_hy.mp4", title: "HY Demo", type: "video" },
  { src: "https://customer-assets.emergentagent.com/job_boring-kilby-5/artifacts/335zmir2_nr.mp4", title: "NR Demo", type: "video" },
  { src: "https://customer-assets.emergentagent.com/job_boring-kilby-5/artifacts/z0k5jhtb_test1.mov", title: "Test Track", type: "video" },
  { src: "https://customer-assets.emergentagent.com/job_boring-kilby-5/artifacts/rr762f0x_Untitled.mov", title: "Untitled", type: "video" },
  { src: "https://customer-assets.emergentagent.com/job_boring-kilby-5/artifacts/0pi627wf_milky25vert.mp4", title: "Milky25", type: "video" },
  { src: "/demos/set_sail_demo.mp3", title: "Set Sail", type: "audio", genre: "Synthwave" },
  { src: "/demos/straight_forward_demo.mp3", title: "Straight Forward", type: "audio", genre: "Synthwave" },
  { src: "/demos/tropical_house_demo.mp3", title: "I Don't Like House But...", type: "audio", genre: "Tropical House" },
  { src: "/demos/alien_demo.mp3", title: "Alien Main Theme", type: "audio", genre: "VGM" },
  { src: "/demos/dangerous_foe_demo.mp3", title: "Dangerous Foe", type: "audio", genre: "Neurofunk" },
  { src: "/demos/euphoric_twist_demo.mp3", title: "Euphoric Twist", type: "audio", genre: "Melodic Techno" },
  { src: "/demos/sent_to_space_demo.mp3", title: "Sent to Space", type: "audio", genre: "Downtempo / Chillout" },
  { src: "/demos/technical_problems_demo.mp3", title: "Technical Problems", type: "audio", genre: "Midtempo / Electro" },
  { src: "/demos/to_the_moon_demo.mp3", title: "To the Moon", type: "audio", genre: "Breakbeat / Electro" },
  { src: "/demos/track1_demo.mp3", title: "Track 1", type: "audio", genre: "Melodic House" },
  { src: "/demos/funtasia_demo.mp3", title: "Funtasia", type: "audio", genre: "Dance / Electro" },
  { src: "/demos/lava_demo.mp3", title: "Lava Track", type: "audio", genre: "Electro / Breaks" },
  { src: "/demos/dustland_demo.mp3", title: "Dustland", type: "audio", genre: "Breakbeat / Electro" },
  { src: "/demos/nefarious_demo.mp3", title: "Nefarious", type: "audio", genre: "Tech Trance" },
  { src: "/demos/ancestors_demo.mp3", title: "Our Ancestors", type: "audio", genre: "Synthwave" },
  { src: "/demos/fear_demo.mp3", title: "Fear Fabrication", type: "audio", genre: "Trance" },
  { src: "/demos/dream_demo.mp3", title: "Dream", type: "audio", genre: "Dance" },
];

const genres = [
  "Synthwave", "Retrowave", "Outrun", "Darksynth", "Trance", "Tech Trance", "Techno", "Tech House",
  "Psytrance", "Drum & Bass", "Neurofunk", "Chiptune", "Dubstep", "Electro", "Electro House", 
  "Progressive House", "Tropical House", "Melodic House", "Deep House", "House", "Melodic Techno",
  "Breakbeat", "Breaks", "Midtempo", "Downtempo", "Chillout", "Horror Synth", "EDM", "Dance", "Eurobeat", "VGM"
];

const SocialIcon = ({ type }) => {
  const icons = {
    facebook: (
      <svg viewBox="0 0 24 24" className="w-5 h-5 fill-current">
        <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
      </svg>
    ),
    instagram: (
      <svg viewBox="0 0 24 24" className="w-5 h-5 fill-current">
        <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
      </svg>
    ),
    soundcloud: (
      <svg viewBox="0 0 24 24" className="w-5 h-5 fill-current">
        <path d="M1.175 12.225c-.051 0-.094.046-.101.1l-.233 2.154.233 2.105c.007.058.05.098.101.098.05 0 .09-.04.099-.098l.255-2.105-.27-2.154c-.009-.06-.052-.1-.084-.1zm-.899 1.025c-.051 0-.091.039-.099.085l-.157 1.044.169 1.025c0 .053.039.09.09.09.052 0 .09-.037.099-.085l.189-1.03-.189-1.044c-.009-.046-.047-.085-.099-.085h-.003zm1.803-.075c-.059 0-.105.046-.112.1l-.21 2.008.223 1.962c.007.063.053.105.112.105.054 0 .1-.042.11-.105l.248-1.962-.248-2.008c-.01-.053-.056-.1-.11-.1h-.013zm.9-.067c-.063 0-.114.051-.119.109l-.193 2.075.193 2.017c.005.063.056.109.119.109.063 0 .114-.046.12-.109l.218-2.017-.218-2.075c-.006-.058-.057-.109-.12-.109zm.9-.14c-.066 0-.121.056-.127.115l-.169 2.16.177 2.097c.006.066.061.117.127.117.066 0 .12-.051.127-.117l.2-2.097-.2-2.16c-.007-.059-.061-.115-.127-.115h-.008zm.9-.084c-.073 0-.127.06-.133.12l-.153 2.244.161 2.144c.006.07.06.12.133.12.073 0 .127-.05.133-.12l.181-2.144-.189-2.244c-.006-.06-.06-.12-.133-.12zm.9-.048c-.077 0-.135.063-.139.127l-.136 2.292.144 2.182c.004.073.062.127.139.127.077 0 .135-.054.14-.127l.161-2.182-.169-2.292c-.004-.064-.062-.127-.14-.127zm.9-.006c-.08 0-.14.067-.144.133l-.12 2.298.128 2.218c.004.077.064.133.144.133.08 0 .14-.056.144-.133l.145-2.218-.153-2.298c-.004-.066-.064-.133-.144-.133zm.901.012c-.084 0-.147.07-.15.14l-.104 2.286.112 2.254c.003.084.066.144.15.144.084 0 .147-.06.15-.144l.127-2.254-.127-2.286c-.003-.07-.066-.14-.15-.14h-.008zm.9.042c-.088 0-.152.074-.155.147l-.088 2.244.096 2.287c.003.088.067.147.155.147.088 0 .152-.059.155-.147l.11-2.287-.11-2.244c-.003-.073-.067-.147-.155-.147h-.008zm1.364-.144c-.096 0-.17.08-.173.16l-.08 2.388.088 2.32c.003.088.077.16.173.16.096 0 .17-.072.173-.16l.1-2.32-.1-2.388c-.003-.08-.077-.16-.173-.16zm.608.14c-.1 0-.177.084-.18.168l-.072 2.248.08 2.356c.003.092.08.168.18.168.1 0 .176-.076.18-.168l.092-2.356-.092-2.248c-.004-.084-.08-.168-.18-.168h-.008zm.9-.028c-.104 0-.184.088-.188.176l-.056 2.276.064 2.392c.004.096.084.176.188.176.104 0 .184-.08.188-.176l.072-2.392-.072-2.276c-.004-.088-.084-.176-.188-.176zm.9-.012c-.108 0-.192.092-.196.184l-.04 2.288.048 2.426c.004.1.088.184.196.184.108 0 .192-.084.196-.184l.056-2.426-.056-2.288c-.004-.092-.088-.184-.196-.184zm6.187-1.222c-.408 0-.8.076-1.16.213-.24-2.717-2.54-4.848-5.35-4.848-1.336 0-2.588.5-3.544 1.368-.37.336-.463.717-.463 1.092v9.789c.01.63.4 1.137 1.017 1.185h9.5c2.163 0 3.917-1.748 3.917-3.905 0-2.156-1.754-3.894-3.917-3.894z"/>
      </svg>
    ),
    youtube: (
      <svg viewBox="0 0 24 24" className="w-5 h-5 fill-current">
        <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
      </svg>
    ),
    tiktok: (
      <svg viewBox="0 0 24 24" className="w-5 h-5 fill-current">
        <path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z"/>
      </svg>
    ),
    twitter: (
      <svg viewBox="0 0 24 24" className="w-5 h-5 fill-current">
        <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
      </svg>
    ),
    spotify: (
      <svg viewBox="0 0 24 24" className="w-5 h-5 fill-current">
        <path d="M12 0C5.4 0 0 5.4 0 12s5.4 12 12 12 12-5.4 12-12S18.66 0 12 0zm5.521 17.34c-.24.359-.66.48-1.021.24-2.82-1.74-6.36-2.101-10.561-1.141-.418.122-.779-.179-.899-.539-.12-.421.18-.78.54-.9 4.56-1.021 8.52-.6 11.64 1.32.42.18.479.659.301 1.02zm1.44-3.3c-.301.42-.841.6-1.262.3-3.239-1.98-8.159-2.58-11.939-1.38-.479.12-1.02-.12-1.14-.6-.12-.48.12-1.021.6-1.141C9.6 9.9 15 10.561 18.72 12.84c.361.181.54.78.241 1.2zm.12-3.36C15.24 8.4 8.82 8.16 5.16 9.301c-.6.179-1.2-.181-1.38-.721-.18-.601.18-1.2.72-1.381 4.26-1.26 11.28-1.02 15.721 1.621.539.3.719 1.02.419 1.56-.299.421-1.02.599-1.559.3z"/>
      </svg>
    ),
    linkedin: (
      <svg viewBox="0 0 24 24" className="w-5 h-5 fill-current">
        <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
      </svg>
    ),
    newgrounds: (
      <svg viewBox="0 0 24 24" className="w-5 h-5 fill-current">
        <path d="M12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12S18.627 0 12 0zm4.5 16.5c-1.5 1.5-3.5 2-5.5 2s-4-0.5-5.5-2c-1.5-1.5-2-3.5-2-5.5s0.5-4 2-5.5c1.5-1.5 3.5-2 5.5-2s4 0.5 5.5 2c1.5 1.5 2 3.5 2 5.5s-0.5 4-2 5.5z"/>
      </svg>
    ),
  };
  return icons[type] || null;
};

const DemoCard = ({ demo, index, activeDemo, setActiveDemo }) => {
  const mediaRef = useRef(null);
  const isActive = activeDemo === index;
  const isAudio = demo.type === "audio";

  useEffect(() => {
    if (mediaRef.current) {
      if (isActive) {
        mediaRef.current.muted = false;
        mediaRef.current.play().catch(() => {
          mediaRef.current.muted = true;
          mediaRef.current.play().then(() => {
            mediaRef.current.muted = false;
          }).catch(() => {});
        });
      } else {
        mediaRef.current.pause();
        mediaRef.current.currentTime = 0;
        mediaRef.current.muted = true;
      }
    }
  }, [isActive]);

  const handleClick = () => {
    if (isActive) {
      setActiveDemo(null);
    } else {
      setActiveDemo(index);
    }
  };

  const toggleMute = (e) => {
    e.stopPropagation();
    if (mediaRef.current) {
      mediaRef.current.muted = !mediaRef.current.muted;
    }
  };

  const badgeLabel = isActive ? "PLAYING" : isAudio ? "AUDIO" : "DEMO";

  return (
    <div 
      className="demo-card"
      data-testid={`demo-card-${index}`}
      style={{ animationDelay: `${index * 0.1}s` }}
    >
      <div className={`demo-video-container ${isAudio ? 'audio-only' : ''}`} onClick={handleClick}>
        {isAudio ? (
          <>
            <audio ref={mediaRef} src={demo.src} loop muted />
            <div className="audio-visual">
              <div className={`audio-bars ${isActive ? 'playing' : ''}`}>
                {[...Array(5)].map((_, i) => (
                  <div key={i} className="bar" style={{ animationDelay: `${i * 0.1}s` }} />
                ))}
              </div>
              {demo.genre && <span className="genre-label">{demo.genre}</span>}
            </div>
          </>
        ) : (
          <video 
            ref={mediaRef}
            src={demo.src}
            loop
            muted
            playsInline
            className="demo-video"
          />
        )}
        <div className={`demo-overlay ${isActive ? 'playing' : ''}`}>
          {!isActive && (
            <div className="play-icon">
              <Play className="w-6 h-6" />
            </div>
          )}
        </div>
        <div className="demo-controls">
          <button 
            onClick={toggleMute}
            className="mute-btn"
            data-testid={`mute-btn-${index}`}
          >
            {isActive ? <Volume2 className="w-4 h-4" /> : <VolumeX className="w-4 h-4" />}
          </button>
        </div>
        {!isAudio && <div className="demo-scanlines"></div>}
      </div>
      <div className="demo-info">
        <span className="demo-title">{demo.title}</span>
        <span className="demo-badge">{badgeLabel}</span>
      </div>
    </div>
  );
};

const VideoCard = ({ video, isActive, onClick }) => {
  return (
    <div 
      data-testid={`video-card-${video.id}`}
      onClick={onClick}
      className={`video-thumb ${isActive ? 'active' : ''}`}
    >
      <div className="video-thumb-inner">
        <img 
          src={`https://img.youtube.com/vi/${video.id}/mqdefault.jpg`}
          alt={video.title}
        />
        <div className="video-thumb-overlay">
          <Play className="w-6 h-6" />
        </div>
        <div className="thumb-scanlines"></div>
      </div>
      <p className="video-thumb-title">{video.title}</p>
    </div>
  );
};

// Obfuscate email to prevent bot scraping — assembled at runtime
const getEmail = () => {
  const u = "kennethnordsveen";
  const d = "gmail.com";
  return `${u}@${d}`;
};

// How long the "message sent" confirmation stays visible after the mailto is triggered
const SENT_FLASH_MS = 4000;

const ContactForm = () => {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [projectType, setProjectType] = useState("");
  const [message, setMessage] = useState("");
  const [honeypot, setHoneypot] = useState("");  // Bot trap
  const [errors, setErrors] = useState({});
  const [sent, setSent] = useState(false);

  const validate = () => {
    const errs = {};
    if (!name.trim() || name.trim().length < 2) errs.name = "Please enter your name";
    if (!email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) errs.email = "Please enter a valid email";
    if (!message.trim() || message.trim().length < 20) errs.message = "Please tell me a bit about your project (min 20 characters)";
    return errs;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Honeypot check — if filled, it's a bot; silently pretend to succeed
    if (honeypot) {
      setSent(true);
      return;
    }

    const errs = validate();
    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      return;
    }
    setErrors({});

    const subject = `Music Inquiry${projectType ? ` — ${projectType}` : ""} — ${name}`;
    const body = `Hi Kenneth,

Name: ${name}
Reply-to: ${email}
${projectType ? `Project type: ${projectType}\n` : ""}
${message}

— Sent from fungistvgm.com`;

    const mailtoUrl = `mailto:${getEmail()}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    window.location.href = mailtoUrl;
    setSent(true);
    setTimeout(() => setSent(false), SENT_FLASH_MS);
  };

  return (
    <form className="contact-form" onSubmit={handleSubmit} data-testid="contact-form" noValidate>
      {/* Honeypot — hidden from humans, bots fill it */}
      <input
        type="text"
        name="website"
        tabIndex="-1"
        autoComplete="off"
        className="contact-honeypot"
        aria-hidden="true"
        value={honeypot}
        onChange={(e) => setHoneypot(e.target.value)}
      />

      <div className="contact-form-row">
        <div className="contact-field">
          <label htmlFor="cf-name">Name *</label>
          <input
            id="cf-name"
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            data-testid="cf-name-input"
            className={errors.name ? "error" : ""}
            placeholder="Your name"
            maxLength="80"
            required
          />
          {errors.name && <span className="contact-error" data-testid="cf-name-error">{errors.name}</span>}
        </div>

        <div className="contact-field">
          <label htmlFor="cf-email">Your Email *</label>
          <input
            id="cf-email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            data-testid="cf-email-input"
            className={errors.email ? "error" : ""}
            placeholder="you@studio.com"
            maxLength="120"
            required
          />
          {errors.email && <span className="contact-error" data-testid="cf-email-error">{errors.email}</span>}
        </div>
      </div>

      <div className="contact-field">
        <label htmlFor="cf-project">Project Type <span className="cf-optional">(optional)</span></label>
        <select
          id="cf-project"
          value={projectType}
          onChange={(e) => setProjectType(e.target.value)}
          data-testid="cf-project-select"
        >
          <option value="">-- select --</option>
          <option value="Videogame OST">Videogame OST</option>
          <option value="Game Jam">Game Jam</option>
          <option value="Single Track / Boss Theme">Single Track / Boss Theme</option>
          <option value="Video / Film / YouTube">Video / Film / YouTube</option>
          <option value="Podcast / Stream Music">Podcast / Stream Music</option>
          <option value="Something else">Something else</option>
        </select>
      </div>

      <div className="contact-field">
        <label htmlFor="cf-message">Message *</label>
        <textarea
          id="cf-message"
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          data-testid="cf-message-input"
          className={errors.message ? "error" : ""}
          placeholder="Tell me about your project — genre vibes, deadline, budget range, links to your game if any..."
          rows="5"
          maxLength="4000"
          required
        />
        <div className="contact-msg-meta">
          {errors.message && <span className="contact-error" data-testid="cf-message-error">{errors.message}</span>}
          <span className="contact-charcount">{message.length} / 4000</span>
        </div>
      </div>

      <button
        type="submit"
        className="contact-submit"
        data-testid="contact-submit-btn"
        disabled={sent}
      >
        {sent ? (
          <>
            <Check className="w-5 h-5" />
            <span>Email client opened!</span>
          </>
        ) : (
          <>
            <Send className="w-5 h-5" />
            <span>Send Message</span>
          </>
        )}
      </button>

      <p className="contact-form-note">
        Opens your email app with everything pre-filled. Just hit send.
      </p>
    </form>
  );
};

// Email button that assembles the address in JS so scrapers can't grab it
const EmailButton = () => {
  const [emailVisible, setEmailVisible] = useState(false);
  const email = getEmail();

  return (
    <button
      type="button"
      className="contact-btn contact-btn-obf"
      data-testid="contact-email-btn"
      onClick={() => {
        setEmailVisible(true);
        window.location.href = `mailto:${email}?subject=${encodeURIComponent("Music Inquiry")}`;
      }}
      aria-label="Reveal email address"
    >
      <Mail className="w-5 h-5" />
      <span>{emailVisible ? email : "Click to reveal email"}</span>
    </button>
  );
};


function FungistLanding() {
  const [activeVideo, setActiveVideo] = useState(youtubeVideos[0]);
  const [scrolled, setScrolled] = useState(false);
  const [activeDemo, setActiveDemo] = useState(null);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div className="app-container" data-testid="fungist-landing">
      {/* Pixel Grid Background */}
      <div className="pixel-bg"></div>
      
      {/* Floating Social Bar */}
      <nav 
        data-testid="social-nav"
        className={`nav-bar ${scrolled ? 'scrolled' : ''}`}
      >
        <div className="nav-content">
          <div className="nav-brand">
            <img 
              src="https://ugc.production.linktr.ee/6873b772-5689-429c-bc2d-a509b448dd3d_xJdkEVPZXKkJNCjNCdNl--1--ki43c.jpeg" 
              alt="Fungist VGM"
              className="nav-avatar"
            />
            <span className="nav-title">FUNGIST<span className="text-accent">VGM</span></span>
          </div>
          <div className="nav-socials">
            {socialLinks.slice(0, 7).map((link) => (
              <a
                key={link.name}
                href={link.url}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`social-${link.icon}`}
                className="nav-social-link"
                title={link.name}
              >
                <SocialIcon type={link.icon} />
              </a>
            ))}
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="hero-section" data-testid="hero-section">
        <div className="hero-content">
          <div className="hero-header">
            <div className="glitch-container">
              <h1 className="hero-title glitch" data-text="FUNGIST">FUNGIST</h1>
            </div>
            <h2 className="hero-subtitle">
              <span className="pixel-text">VIDEO GAME MUSIC</span>
            </h2>
            <p className="hero-tagline">
              Music That Sticks With You Forever
            </p>
          </div>

          {/* Main Video Player */}
          <div className="main-player" data-testid="main-video-player">
            <div className="player-frame">
              <div className="player-corner tl"></div>
              <div className="player-corner tr"></div>
              <div className="player-corner bl"></div>
              <div className="player-corner br"></div>
              <div className="video-wrapper">
                <iframe
                  src={`https://www.youtube.com/embed/${activeVideo.id}?autoplay=0&rel=0&modestbranding=1`}
                  title={activeVideo.title}
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              </div>
            </div>
            <div className="player-info">
              <span className="now-playing">NOW PLAYING</span>
              <h3 className="track-title">{activeVideo.title}</h3>
            </div>
          </div>

          {/* Video Thumbnails */}
          <div className="video-grid" data-testid="video-thumbnails">
            {youtubeVideos.map((video) => (
              <VideoCard 
                key={video.id}
                video={video}
                isActive={activeVideo.id === video.id}
                onClick={() => setActiveVideo(video)}
              />
            ))}
          </div>
        </div>
      </section>

      {/* About Section */}
      <section className="about-section" data-testid="about-section">
        <div className="about-content">
          <div className="about-badge">ABOUT THE COMPOSER</div>
          <div className="about-text">
            <p className="lead">
              Having background music in a game is one thing, but having 
              <span className="highlight"> memorable music with melodies that stick with you for the rest of your life</span> — 
              that's the KEY to nostalgia.
            </p>
            <p>
              With almost <strong>20 years of experience</strong> as a music producer, 
              I'm now aiming towards the game industry as videogame music has always been my inspiration. 
              I create music for videogame developers, movie clips, content creators, and beyond.
            </p>
            <p className="italic">
              If you like my music and think my style would suit your game — let's talk.
            </p>
          </div>
          <div className="signature">— KENNETH NORDSVEEN —</div>
          <div className="alias-text">aka Fungist VGM / B-laze</div>
          
          {/* Genre Tags */}
          <div className="genres-strip" data-testid="genres-section">
            <div className="genres-label">STYLES</div>
            <div className="genres-tags">
              {genres.map((genre, index) => (
                <span key={genre} className="genre-tag" style={{ animationDelay: `${index * 0.05}s` }}>
                  {genre}
                </span>
              ))}
            </div>
          </div>

          {/* Game Types */}
          <div className="game-types-section" data-testid="game-types-section">
            <div className="genres-label">PERFECT FOR</div>
            <p className="game-types-intro">
              Whether you're building a fast-paced shooter or a chill puzzle game, I can create the sound that fits your vision.
            </p>
            <div className="game-types-grid">
              <div className="game-type-card">
                <span className="game-type-icon">🕹️</span>
                <h4>Retro / Arcade</h4>
                <p>Synthwave & Outrun vibes for pixel art and neo-retro aesthetics</p>
              </div>
              <div className="game-type-card">
                <span className="game-type-icon">🏎️</span>
                <h4>Racing Games</h4>
                <p>High-energy tracks that match the speed and intensity</p>
              </div>
              <div className="game-type-card">
                <span className="game-type-icon">👾</span>
                <h4>Platformers</h4>
                <p>Electro & Breakbeat energy for action-packed jumping</p>
              </div>
              <div className="game-type-card">
                <span className="game-type-icon">🔫</span>
                <h4>Shooters / Action</h4>
                <p>Intense beats for top-down chaos and bullet-hell</p>
              </div>
              <div className="game-type-card">
                <span className="game-type-icon">👹</span>
                <h4>Boss Fights</h4>
                <p>Neurofunk & Tech Trance for epic, memorable battles</p>
              </div>
              <div className="game-type-card">
                <span className="game-type-icon">🧩</span>
                <h4>Puzzle / Casual</h4>
                <p>Downtempo & Chillout for relaxed thinking games</p>
              </div>
              <div className="game-type-card">
                <span className="game-type-icon">👻</span>
                <h4>Horror / Suspense</h4>
                <p>Dark atmospheres and tension-building soundscapes</p>
              </div>
              <div className="game-type-card">
                <span className="game-type-icon">🤖</span>
                <h4>Cyberpunk / Sci-Fi</h4>
                <p>Futuristic Synthwave & Darksynth for neon worlds</p>
              </div>
              <div className="game-type-card">
                <span className="game-type-icon">🎵</span>
                <h4>Rhythm Games</h4>
                <p>Dance & Trance with clear beats to sync gameplay</p>
              </div>
              <div className="game-type-card">
                <span className="game-type-icon">🗺️</span>
                <h4>Exploration</h4>
                <p>Melodic & atmospheric tracks for open worlds</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Demo Reels Gallery */}
      <section className="demos-section" data-testid="demos-section">
        <div className="section-header">
          <div className="section-badge">AUDIO DEMOS</div>
          <h2 className="section-title">Quick Samples</h2>
          <p className="section-desc">Click to play with sound</p>
        </div>
        
        <div className="demos-grid">
          {demoReels.map((demo, index) => (
            <DemoCard 
              key={demo.title} 
              demo={demo} 
              index={index} 
              activeDemo={activeDemo}
              setActiveDemo={setActiveDemo}
            />
          ))}
        </div>
      </section>

      {/* Music Section */}
      <section className="music-section" data-testid="music-section">
        <div className="section-header">
          <div className="section-badge">DISCOGRAPHY</div>
          <h2 className="section-title">Explore My Music</h2>
          <p className="section-desc">Full albums & game soundtracks</p>
        </div>
        
        <div className="playlist-grid">
          {/* Super Trunko Go */}
          <div className="playlist-card" data-testid="playlist-trunko">
            <div className="card-frame">
              <div className="playlist-header">
                <img 
                  src="https://static.wixstatic.com/media/d28529_65223ff12e334dad97ff98d162399062~mv2.jpg" 
                  alt="Super Trunko Go"
                  className="playlist-image"
                />
                <div className="playlist-badge">GAME OST</div>
              </div>
              <div className="playlist-content">
                <h3>Super Trunko Go</h3>
                <p>Top-down shooter by 88polygons. I produced the entire music library for this indie game!</p>
                <a 
                  href="https://store.steampowered.com/app/1934660/Super_Trunko_Go/" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="playlist-link"
                >
                  View on Steam <ExternalLink className="w-4 h-4" />
                </a>
              </div>
              <div className="soundcloud-embed">
                <iframe 
                  title="Super Trunko Go OST"
                  width="100%" 
                  height="250" 
                  scrolling="no" 
                  frameBorder="no" 
                  allow="autoplay"
                  src="https://w.soundcloud.com/player/?url=https%3A//api.soundcloud.com/playlists/soundcloud%253Aplaylists%253A1758340266&color=%2300ff88&auto_play=false&hide_related=true&show_comments=false&show_user=true&show_reposts=false&show_teaser=false&visual=true"
                />
              </div>
            </div>
          </div>

          {/* Simple Nostalgia */}
          <div className="playlist-card" data-testid="playlist-nostalgia">
            <div className="card-frame">
              <div className="playlist-header">
                <img 
                  src="https://static.wixstatic.com/media/d28529_332f3029fce041389c7d9029e5bbdaa8~mv2.png" 
                  alt="Simple Nostalgia"
                  className="playlist-image"
                />
                <div className="playlist-badge">ALBUM</div>
              </div>
              <div className="playlist-content">
                <h3>Simple Nostalgia</h3>
                <p>Showcasing how simple and efficient you can make melodic music with memorable, great melodies.</p>
                <a 
                  href="https://soundcloud.com/b-laze/sets/fungistvgm-simple-nostalgia" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="playlist-link"
                >
                  Listen on SoundCloud <ExternalLink className="w-4 h-4" />
                </a>
              </div>
              <div className="soundcloud-embed">
                <iframe 
                  title="Simple Nostalgia Album"
                  width="100%" 
                  height="250" 
                  scrolling="no" 
                  frameBorder="no" 
                  allow="autoplay"
                  src="https://w.soundcloud.com/player/?url=https%3A//api.soundcloud.com/playlists/soundcloud%253Aplaylists%253A1576039489&color=%2300ff88&auto_play=false&hide_related=true&show_comments=false&show_user=true&show_reposts=false&show_teaser=false&visual=true"
                />
              </div>
            </div>
          </div>

          {/* Promotional Collection */}
          <div className="playlist-card full-width" data-testid="playlist-promo">
            <div className="card-frame">
              <div className="playlist-header">
                <img 
                  src="https://static.wixstatic.com/media/d28529_937da4b234274f82abdc147737d3f722~mv2.png" 
                  alt="Promotional Collection"
                  className="playlist-image"
                />
                <div className="playlist-badge">SHOWCASE</div>
              </div>
              <div className="playlist-content">
                <h3>Promotional Collection</h3>
                <p>Random original and remixed productions, mainly videogames. Showcases many of my different styles!</p>
                <a 
                  href="https://soundcloud.com/b-laze/sets/promotional-collection/s-9gmwaB6bboa" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="playlist-link"
                >
                  Explore All <ExternalLink className="w-4 h-4" />
                </a>
              </div>
              <div className="soundcloud-embed promo-embed">
                <iframe 
                  title="Promotional Collection"
                  width="100%" 
                  height="400" 
                  scrolling="no" 
                  frameBorder="no" 
                  allow="autoplay"
                  src="https://w.soundcloud.com/player/?url=https%3A//api.soundcloud.com/playlists/soundcloud%3Aplaylists%3A1806532903%3Fsecret_token%3Ds-9gmwaB6bboa&color=%2300ff88&auto_play=false&hide_related=true&show_comments=false&show_user=true&show_reposts=false&show_teaser=false&visual=false"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="contact-section" data-testid="contact-section">
        <div className="contact-content">
          <div className="section-badge">HIRE ME</div>
          <h2 className="section-title">Let's Work Together</h2>
          <p className="section-desc">Need music for your game, video, or project? Fill in the form below — I read every message personally.</p>
          
          <ContactForm />

          <div className="contact-alt">
            <span className="contact-alt-label">Prefer email directly?</span>
            <EmailButton />
          </div>

          <div className="social-grid">
            {socialLinks.map((link) => (
              <a
                key={link.name}
                href={link.url}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`footer-social-${link.icon}`}
                className="social-link"
              >
                <SocialIcon type={link.icon} />
                <span>{link.name}</span>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer" data-testid="footer">
        <div className="footer-content">
          <div className="footer-brand">
            <img 
              src="https://ugc.production.linktr.ee/6873b772-5689-429c-bc2d-a509b448dd3d_xJdkEVPZXKkJNCjNCdNl--1--ki43c.jpeg" 
              alt="Fungist VGM"
            />
            <span>© 2025 Fungist VGM</span>
          </div>
          <a 
            href="https://linktr.ee/kenlun" 
            target="_blank" 
            rel="noopener noreferrer"
            className="footer-link"
          >
            Linktree
          </a>
        </div>
      </footer>
    </div>
  );
}

export default FungistLanding;
